import React, { useEffect } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';
import {
  MAX_CHAMPION_FIRST_NAME_LENGTH,
  MAX_CHAMPION_LAST_NAME_LENGTH,
} from '../../../utils/ChampionApplicationFormValidation';
import { FieldHelpTooltip } from '../FieldHelpTooltip';

const PROFILE_PATH = '/user/profile/';

function truncateNameSegment(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength);
}

type FieldErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  communityProfileUrl?: string;
  linkedinUrl?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  emailId: string;
  profileId: string;
  userName: string;
  homeBaseUrl: string;
  errors?: FieldErrors;
};

export const PersonalInfoSection: React.FC<Props> = ({
  values,
  updateField,
  emailId,
  profileId,
  userName,
  homeBaseUrl,
  errors,
}) => {
  useEffect(() => {
    updateField('email', emailId);
    updateField('communityProfileUrl', `${homeBaseUrl}${PROFILE_PATH}${profileId}`);

    if (userName.trim()) {
      const parts = userName.trim().split(/\s+/);
      if (parts.length === 1) {
        updateField(
          'lastName',
          truncateNameSegment(parts[0], MAX_CHAMPION_LAST_NAME_LENGTH),
        );
      } else {
        updateField(
          'firstName',
          truncateNameSegment(
            parts.slice(0, -1).join(' '),
            MAX_CHAMPION_FIRST_NAME_LENGTH,
          ),
        );
        updateField(
          'lastName',
          truncateNameSegment(parts[parts.length - 1], MAX_CHAMPION_LAST_NAME_LENGTH),
        );
      }
    }
  }, [emailId, profileId, userName, homeBaseUrl]);
  return (
    <div css={sectionBoxStyles}>
      <div css={sectionTitleStyles}>Personal information</div>

      <div css={fieldStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="first-name">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          First name
        </label>
        <Textfield
          id="first-name"
          value={values.firstName}
          onChange={(e) =>
            updateField(
              'firstName',
              (e.target as HTMLInputElement).value.slice(0, MAX_CHAMPION_FIRST_NAME_LENGTH),
            )
          }
          isInvalid={!!errors?.firstName}
          isCompact
          maxLength={MAX_CHAMPION_FIRST_NAME_LENGTH}
        />
        {errors?.firstName && <div css={errorStyles}>{errors.firstName}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="last-name">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Last name
        </label>
        <Textfield
          id="last-name"
          value={values.lastName}
          onChange={(e) =>
            updateField(
              'lastName',
              (e.target as HTMLInputElement).value.slice(0, MAX_CHAMPION_LAST_NAME_LENGTH),
            )
          }
          isInvalid={!!errors?.lastName}
          isCompact
          maxLength={MAX_CHAMPION_LAST_NAME_LENGTH}
        />
        {errors?.lastName && <div css={errorStyles}>{errors.lastName}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="email">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Contact email
        </label>
        <Textfield
          id="email"
          type="email"
          value={values.email}
          isDisabled
          isCompact
        />
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="community-profile-url">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Atlassian Community profile URL
          <FieldHelpTooltip
            content="Log in community.atlassian.com and select your profile icon in the top right, then select Profile. Copy and paste the entire URL."
            iconLabel="Atlassian Community profile Help Info"
          />
        </label>
        <Textfield
          id="community-profile-url"
          value={values.communityProfileUrl}
          isDisabled
          isCompact
        />
      </div>

      <div css={fieldTopStyles} className="champion-form-linkedin-url">
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="linkedin-url">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          LinkedIn profile URL
          <FieldHelpTooltip
            content="Valid url example https://www.linkedin.com/in/validProfileId/"
            iconLabel="LinkedIn profile URL Help Info"
          />
        </label>
        <Textfield
          id="linkedin-url"
          placeholder="https://www.linkedin.com/in/yourprofileid/"
          value={values.linkedinUrl}
          onChange={(e) => updateField('linkedinUrl', (e.target as HTMLInputElement).value)}
          isCompact
          isInvalid={!!errors?.linkedinUrl}
          maxLength={255}
        />
        {errors?.linkedinUrl && <div css={errorStyles}>{errors.linkedinUrl}</div>}
      </div>
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const sectionTitleStyles = css({
  display: 'none',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '12px',
  letterSpacing: '0.04em',
  marginBottom: token('space.100'),
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '40px',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.875rem',
  color: '#3e3e3c',
  marginBottom: token('space.050'),
  lineHeight: 1.5,
});

// Form label: smaller, normal weight, tighter spacing
const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
